import { isNewPlaceholderLineItem } from "pages/makeclaim/shared/claimCertHelper";

/**
 * Finds the parent IDs of a leaf node in a tree structure.
 *
 * @param {Array} tree - Array representing the tree structure.
 * @param {string} leafId - ID of the leaf node whose parent path needs to be found.
 * @param {Array} currentPath - Accumulator for building the path (used in recursion).
 * @returns {Array|null} - Array of parent IDs leading to the leaf node, or null if not found.
 */
export function findTreeParentIds(tree, leafId, currentPath = []) {
  for (const node of tree) {
    const newPath = [node.id, ...currentPath];
    if (node.id === leafId) {
      return newPath;
    }
    if (node.childitems && node.childitems.length > 0) {
      const parentPath = findTreeParentIds(node.childitems, leafId, newPath);
      if (parentPath) {
        return parentPath; // Return the parent path if found
      }
    }
  }
  return null;
}

/**
 * Checks if a tree item is a parent (i.e., has child items).
 *
 * @param {Object} item - The tree item to check.
 * @returns {boolean} - True if the item is a parent, otherwise false.
 */
export function checkIsParent(item) {
  if (item.childitems && item.childitems.length !== 0) {
    return true;
  } else {
    return false;
  }
}

/**
 * Adds a new child item to a parent item in a tree structure.
 *
 * @param {Array} arr - Array representing the tree structure.
 * @param {string} targetId - ID of the parent item to which the child item is to be added.
 * @param {Object} newChildItem - The new child item to add.
 * @returns {Object|null} - The updated parent item, or null if the parent item is not found.
 */
export const addChildItemToParent = (arr, targetId, newChildItem) => {
  for (let item of arr) {
    if (item.id === targetId) {
      // Found the target item, add the new child item
      if (!item.childitems) {
        item.childitems = []; // Initialize childitems array if it's null or undefined
      }

      // Check if there's only one child item and it's a placeholder
      if (
        item.childitems.length === 1 &&
        isNewPlaceholderLineItem(item.childitems[0].id)
      ) {
        // Remove the only placeholder child item
        item.childitems = [];
      }

      // Now childitems is either an empty array [] or contains valid items.
      item.childitems.push(newChildItem);
      return item; // Return the updated item
    }
    if (item.childitems) {
      // Recursively search in the child items
      const updatedItem = addChildItemToParent(
        item.childitems,
        targetId,
        newChildItem,
      );
      if (updatedItem) return updatedItem; // Return the updated item if found
    }
  }
  return null; // Return null if the item with targetId is not found
};

/**
 * Finds a tree node by a specific attribute value in a nested tree structure.
 *
 * @param {any} value - Value to search for in the tree.
 * @param {Array} tree - Array representing the tree structure.
 * @param {string} [attr="id"] - Attribute to match the value against (default is "id").
 * @param {string} [childAttr="childitems"] - Attribute representing child nodes (default is "childitems").
 * @returns {Object|null} - The tree node matching the value, or null if not found.
 */
export function findTreeNode(
  value,
  tree,
  attr = "id",
  childAttr = "childitems",
) {
  if (!(tree instanceof Array)) {
    //not a tree
    return null;
  }
  if (tree.length === 0) return;

  for (let i = 0; i < tree.length; i++) {
    const item = tree[i];

    if (item[attr] === value) {
      // if found return
      return item;
    }
    if (item[childAttr] && item[childAttr].length !== 0) {
      const node = findTreeNode(value, item[childAttr], attr, childAttr);
      if (node) {
        // return
        return node;
      }
    }
  }
  return null;
}
